import { useEffect } from 'react';
import { gql } from 'graphql-request';
import { InfiniteData, useMutation } from 'react-query';
import { useGraphQLInfiniteQuery, useGraphQLQuery } from '@frontend/api-payments';
import { RefundEdge, RefundListQueryResponse, RefundModel, RefundSummary } from '@frontend/api-refunds';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { downloadCSV } from '@frontend/media-helpers';
import { paymentsQueryKeys, useMultiQueryKey, useMultiLocationUtils, useMerchants } from '@frontend/payments-hooks';
import { useAppScopeStore, useScopedQuery } from '@frontend/scope';
import { useAlert } from '@frontend/design-system';
import { formatCentsToCurrency } from '../utils/currency-helpers';
import { prettify } from '../utils/date-helpers';
import { getRefundsQueryFilter, GetLocationName } from '../utils/helpers';
import { usePaymentsGraphqlClient } from '../utils/payment-graphql-client';
import { allRefundsQueryString, refundListQueryString } from '../utils/refund-queries';
import { useRefundShallowStore } from './refund-provider';

export const useQueryAllRefunds = () => {
  const { client } = usePaymentsGraphqlClient();
  const { selectedLocationIds: locationIds } = useAppScopeStore();
  const { getMultiQueryKey } = useMultiQueryKey(locationIds);
  const { filter } = useRefundShallowStore('filter');

  const { data, isLoading, refetch } = useScopedQuery<RefundListQueryResponse, Error, RefundModel[]>({
    queryKey: getMultiQueryKey([paymentsQueryKeys.allRefunds, filter]),
    queryFn: () =>
      client.request(
        gql`
          ${allRefundsQueryString}
        `,
        { filter: { ...getRefundsQueryFilter(filter), locationId: locationIds } }
      ),
    select: (data) => (data?.refunds?.edges?.map((e) => e.node) ?? []) as RefundModel[],
  });

  return {
    allRefunds: data,
    allRefundsLoading: isLoading,
    refetch,
  };
};

// the mobile view of refunds uses an infinite paginated list, which is coupled to the infinite query props
export const useRefundSearchInfiniteQuery = <T>({
  select,
}: { select?: (data: InfiniteData<RefundListQueryResponse>) => InfiniteData<RefundListQueryResponse & T> } = {}) => {
  const { client } = usePaymentsGraphqlClient();
  const { filter, numRows, pageParams, resetPageParams } = useRefundShallowStore(
    'filter',
    'numRows',
    'pageParams',
    'setPageParams',
    'resetPageParams'
  );
  const { selectedLocationIds: locationIds } = useAppScopeStore();
  const { allMerchantsLoaded } = useMerchants(locationIds);
  const { getMultiQueryKey } = useMultiQueryKey(locationIds);

  useEffect(() => {
    resetPageParams();
  }, [locationIds]);

  const infiniteQueryProps = useGraphQLInfiniteQuery<RefundListQueryResponse, T>({
    queryKey: getMultiQueryKey(['refunds', filter, numRows]),
    queryFn: (pageParam) =>
      client.request(
        gql`
          ${refundListQueryString}
        `,
        { filter: { ...getRefundsQueryFilter(filter), locationId: locationIds }, pageInfo: pageParam }
      ),
    pageParams,
    getPageInfo: (response?: RefundListQueryResponse) => response?.refunds?.pageInfo,
    enabled: !!allMerchantsLoaded,
    select,
  });

  return infiniteQueryProps;
};

const useRefundSearchQuery = () => {
  const { client } = usePaymentsGraphqlClient();
  const { filter, numRows, pageParams, setPageParams, resetPageParams } = useRefundShallowStore(
    'filter',
    'numRows',
    'pageParams',
    'setPageParams',
    'resetPageParams'
  );
  const { selectedLocationIds: locationIds } = useAppScopeStore();
  const { allMerchantsLoaded } = useMerchants(locationIds);
  const { getMultiQueryKey } = useMultiQueryKey(locationIds);

  useEffect(() => {
    resetPageParams();
  }, [locationIds]);

  const { data: refunds, ...rest } = useGraphQLQuery<RefundListQueryResponse, RefundModel, RefundSummary, RefundEdge>({
    queryKey: getMultiQueryKey(['refunds', filter, numRows]),
    queryFn: (pageParam) =>
      client.request(
        gql`
          ${refundListQueryString}
        `,
        { filter: { ...getRefundsQueryFilter(filter), locationId: locationIds }, pageInfo: pageParam }
      ),
    numRows,
    pageParams,
    setPageParams,
    getPageInfo: (response?: RefundListQueryResponse) => response?.refunds?.pageInfo,
    getEdges: (response?: RefundListQueryResponse) => response?.refunds?.edges,
    getSummary: (response?: RefundListQueryResponse) => response?.refunds?.summary,
    enabled: !!allMerchantsLoaded,
  });

  return {
    refunds,
    ...rest,
  };
};
export const useRefundSearch = useRefundSearchQuery;

export const useExportRefundsQuery = () => {
  const { client } = usePaymentsGraphqlClient();
  const { selectedLocationIds: locationIds } = useAppScopeStore();
  const { getLocationName } = useMultiLocationUtils();
  const { getMultiQueryKey } = useMultiQueryKey(locationIds);
  const { filter } = useRefundShallowStore('filter');
  const alerts = useAlert();
  const { t } = useTranslation('payments');

  const {
    data: refundsForExport,
    mutate: exportRefunds,
    isLoading: isExporting,
  } = useMutation<RefundListQueryResponse>({
    mutationKey: getMultiQueryKey([paymentsQueryKeys.allRefunds, filter]),
    mutationFn: () =>
      client.request(
        gql`
          ${allRefundsQueryString}
        `,
        { filter: { ...getRefundsQueryFilter(filter), locationId: locationIds } }
      ),
    onSuccess: ({ refunds }) => {
      const { edges } = refunds ?? {};
      const transformedData = transformDataForCsvExport(edges, getLocationName);
      downloadCSV(transformedData, 'refunds_list');
    },
    onError: (err) => {
      alerts.error(t('Failed to export refund history'));
      console.error(err);
    },
  });

  return { refundsForExport, isExporting, exportRefunds };
};

const transformDataForCsvExport = (data: RefundEdge[] = [], getLocationName: GetLocationName) => {
  return data.map((refund) => {
    const refundId = refund.node.id || '';
    const refundType = prettify(refund.node.type ?? '');
    const personName = refund.node.person?.name || '';
    const refundAmount = formatCentsToCurrency(refund.node.refundAmount)?.replace(',', '') || 'Not Available';
    const refundDate = formatDate(refund.node.dateRefunded, 'YYYY-MM-D');
    const personId = refund.node.person?.id || '';
    const paymentAmount = formatCentsToCurrency(refund.node.payment?.amount)?.replace(',', '') || 'Not Available';
    const paymentDate = formatDate(refund.node.payment?.date, 'YYYY-MM-D');
    const paymentId = refund.node.payment?.id || '';
    const locationName = getLocationName(refund.node.locationId ?? '') ?? '';

    return {
      'Location Name': locationName,
      'Patient Id': personId,
      Name: personName,
      'Refund Id': refundId,
      'Refund Type': refundType,
      'Refund Amount': refundAmount,
      'Refund Date': refundDate,
      'Payment Id': paymentId,
      'Payment Amount': paymentAmount,
      'Payment Date': paymentDate,
    };
  });
};
